import React, { useMemo } from 'react';
import { Text } from '@vibe/core';
import { NavigationChevronRight, NavigationChevronDown } from '@vibe/icons';
import { format, parseISO, isWeekend } from 'date-fns';
import TimesheetCell from './TimesheetCell';
import { formatHours, formatProgress, getCellCapacity } from '../../utils/timesheet-capacity';
import type {
  TimesheetRow,
  CapacityMap,
} from '../../types/timesheet.types';

export interface TimesheetTimelineProps {
  rows: TimesheetRow[];
  dateColumns: string[];
  capacityMap: CapacityMap;
  expandedRowKeys: Set<string>;
  onToggleRow: (rowKey: string) => void;
  onCellClick?: (row: TimesheetRow, date: string, parentRow?: TimesheetRow) => void;
  isLoading: boolean;
  /** Whether user has selected teams or users in the filter */
  hasSelection: boolean;
  /** Whether to show the Progress column (default: true) */
  showProgress?: boolean;
}

const TimesheetTimeline: React.FC<TimesheetTimelineProps> = ({
  rows,
  dateColumns,
  capacityMap,
  expandedRowKeys,
  onToggleRow,
  onCellClick,
  isLoading,
  hasSelection,
  showProgress = true,
}) => {
  // Compute column totals
  const columnTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const date of dateColumns) {
      totals[date] = 0;
    }
    for (const row of rows) {
      // Only sum top-level rows (depth 0) to avoid double-counting
      for (const date of dateColumns) {
        const cell = row.cells[date];
        if (cell) {
          totals[date] += cell.actual;
        }
      }
    }
    return totals;
  }, [rows, dateColumns]);

  const grandTotal = useMemo(
    () => rows.reduce((sum, r) => sum + r.totalActual, 0),
    [rows],
  );

  // Pre-compute date metadata once (avoids repeated parseISO in render loops)
  const dateColumnMeta = useMemo(
    () =>
      dateColumns.map((date) => {
        const dt = parseISO(date);
        return {
          date,
          dayNumber: format(dt, 'd'),
          dayLetter: format(dt, 'EEE').charAt(0),
          isWeekend: isWeekend(dt),
        };
      }),
    [dateColumns],
  );

  // Flatten visible rows (respecting expand/collapse state) and track parents
  const { visibleRows, parentMap } = useMemo(() => {
    const result: TimesheetRow[] = [];
    const pMap = new Map<string, TimesheetRow>();
    const flatten = (rowList: TimesheetRow[], parent?: TimesheetRow) => {
      for (const row of rowList) {
        result.push(row);
        if (parent) pMap.set(row.rowKey, parent);
        if (row.expandable && expandedRowKeys.has(row.rowKey) && row.children) {
          flatten(row.children, row);
        }
      }
    };
    flatten(rows);
    return { visibleRows: result, parentMap: pMap };
  }, [rows, expandedRowKeys]);

  if (rows.length === 0 && !isLoading) {
    return (
      <div className="rp-ts-timeline__empty">
        <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
          {hasSelection
            ? 'No timesheet data found for the selected period.'
            : 'Select teams or users to view timesheet data.'}
        </Text>
      </div>
    );
  }

  return (
    <div className="rp-ts-timeline">
      <div className="rp-ts-timeline__scroll">
        <table className="rp-ts-timeline__table">
          {/* ── Header ──────────────────────────────────────────────── */}
          <thead>
            <tr>
              <th className="rp-ts-th rp-ts-th--entity">
                {/* Entity name column */}
              </th>
              {showProgress && (
                <th className="rp-ts-th rp-ts-th--progress">Progress (%)</th>
              )}
              <th className="rp-ts-th rp-ts-th--sum">Sum, h</th>
              {dateColumnMeta.map((col) => (
                <th
                  key={col.date}
                  className={`rp-ts-th rp-ts-th--day ${col.isWeekend ? 'rp-ts-th--weekend' : ''}`}
                >
                  <div className="rp-ts-th__day-header">
                    <span className="rp-ts-th__day-number">
                      {col.dayNumber}
                    </span>
                    <span className="rp-ts-th__day-name">
                      {col.dayLetter}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          {/* ── Body ────────────────────────────────────────────────── */}
          <tbody>
            {visibleRows.map((row) => {
              const isParent = row.depth === 0 && row.expandable;
              const isExpanded = expandedRowKeys.has(row.rowKey);
              const isChild = row.depth > 0;
              const userId =
                row.entityType === 'user' ? row.entityId : null;

              return (
                <tr
                  key={row.rowKey}
                  className={`rp-ts-row ${isParent ? 'rp-ts-row--parent' : ''} ${isChild ? 'rp-ts-row--child' : ''}`}
                >
                  {/* Entity name cell */}
                  <td className="rp-ts-td rp-ts-td--entity">
                    <div
                      className="rp-ts-entity"
                      style={{ paddingLeft: row.depth * 24 }}
                    >
                      {/* Expand/collapse toggle */}
                      {row.expandable ? (
                        <button
                          className="rp-ts-entity__toggle"
                          onClick={() => onToggleRow(row.rowKey)}
                          title={isExpanded ? 'Collapse' : 'Expand'}
                        >
                          {isExpanded ? (
                            <NavigationChevronDown />
                          ) : (
                            <NavigationChevronRight />
                          )}
                        </button>
                      ) : (
                        <span className="rp-ts-entity__toggle-spacer" />
                      )}

                      {/* Avatar for user rows */}
                      {row.entityType === 'user' && (
                        <span
                          className="rp-ts-entity__avatar"
                          title={row.label}
                        >
                          {row.label.charAt(0).toUpperCase()}
                        </span>
                      )}

                      {/* Name + badge */}
                      <span className="rp-ts-entity__name" title={row.label}>
                        {row.label}
                      </span>
                      {row.countBadge && (
                        <span className="rp-ts-entity__badge">
                          ({row.countBadge})
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Progress cell */}
                  {showProgress && (
                    <td className="rp-ts-td rp-ts-td--progress">
                      {row.progress !== null ? (
                        <div className="rp-ts-progress">
                          <div
                            className={`rp-ts-progress__bar ${
                              row.progress > 100
                                ? 'rp-ts-progress__bar--over'
                                : row.progress >= 80
                                  ? 'rp-ts-progress__bar--good'
                                  : 'rp-ts-progress__bar--low'
                            }`}
                            style={{
                              width: `${Math.min(row.progress, 100)}%`,
                            }}
                          />
                          <span className="rp-ts-progress__text">
                            {formatProgress(row.progress)}
                          </span>
                        </div>
                      ) : (
                        <span className="rp-ts-cell__empty">-</span>
                      )}
                    </td>
                  )}

                  {/* Sum cell */}
                  <td className="rp-ts-td rp-ts-td--sum">
                    <span className="rp-ts-sum__value">
                      {formatHours(row.totalActual)}
                    </span>
                  </td>

                  {/* Day cells */}
                  {dateColumnMeta.map((col) => {
                    const cellValue = row.cells[col.date];
                    const actual = cellValue?.actual ?? 0;
                    const capInfo = userId
                      ? getCellCapacity(capacityMap, userId, col.date, actual)
                      : null;
                    return (
                      <TimesheetCell
                        key={col.date}
                        value={cellValue}
                        capInfo={capInfo}
                        isWeekendColumn={col.isWeekend}
                        onClick={
                          onCellClick && actual > 0
                            ? () => onCellClick(row, col.date, parentMap.get(row.rowKey))
                            : undefined
                        }
                      />
                    );
                  })}
                </tr>
              );
            })}
          </tbody>

          {/* ── Footer ──────────────────────────────────────────────── */}
          <tfoot>
            <tr className="rp-ts-footer">
              <td className="rp-ts-td rp-ts-td--entity rp-ts-footer__label">
                <Text
                  type={Text.types.TEXT2}
                  weight={Text.weights.BOLD}
                  style={{ color: '#676879' }}
                >
                  Total
                </Text>
              </td>
              {showProgress && <td className="rp-ts-td rp-ts-td--progress" />}
              <td className="rp-ts-td rp-ts-td--sum rp-ts-footer__sum">
                <span className="rp-ts-sum__value rp-ts-sum__value--bold">
                  {formatHours(grandTotal)}
                </span>
              </td>
              {dateColumnMeta.map((col) => {
                const total = columnTotals[col.date] ?? 0;
                return (
                  <td
                    key={col.date}
                    className={`rp-ts-td rp-ts-td--day rp-ts-footer__day ${col.isWeekend ? 'rp-ts-td--weekend' : ''}`}
                  >
                    <span
                      className={
                        total > 0
                          ? 'rp-ts-cell__value'
                          : 'rp-ts-cell__empty'
                      }
                    >
                      {formatHours(total)}
                    </span>
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default TimesheetTimeline;
